import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

import {
  HANDOFF_SOURCE_MEANINGS,
  MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS,
  MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX,
  MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX,
  countHandoffSectionEdits,
  isVendorInaccessibleUrl,
  looksLikeHandoffUrl,
  type HandoffDefaultSource,
  type MarketplaceOrderHandoffDefaultItemMeta,
  type MarketplaceOrderHandoffDefaults,
} from "@nexus/contracts";
import {
  Badge,
  Icon,
  Label,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";

const SOURCE_I18N_KEYS: Record<HandoffDefaultSource, string> = {
  "Per order": "marketplace.handoff.sources.perOrder",
  Package: "marketplace.handoff.sources.package",
  Folder: "marketplace.handoff.sources.folder",
};

const SECTION_COPY = {
  rules: {
    titleKey: "marketplace.handoff.sections.rules.title",
    titleDefault: "Brand rules",
    hintKey: "marketplace.handoff.sections.rules.hint",
    hintDefault:
      "Default guidelines for orders set by the client, expand this to adjust the guidelines for this order",
  },
  brandKit: {
    titleKey: "marketplace.handoff.sections.brandKit.title",
    titleDefault: "Brand kit",
    hintKey: "marketplace.handoff.sections.brandKit.hint",
    hintDefault:
      "Provides folders or links to the brand assets for the partner's use during production",
  },
} as const;

export function emptyHandoffDefaultsFormState(): MarketplaceOrderHandoffDefaults {
  return {
    brandGuidelines: "",
    techSpecs: "",
    policyRestrictions: "",
    dosAndDonts: "",
    productAccuracy: "",
    thumbnailGuidelines: "",
    fontsLicenceProof: "",
    coloursHex: "",
    logos: "",
  };
}

export function handoffDefaultsFromApi(
  raw: MarketplaceOrderHandoffDefaults | null | undefined,
): MarketplaceOrderHandoffDefaults {
  if (!raw) return emptyHandoffDefaultsFormState();
  return { ...emptyHandoffDefaultsFormState(), ...raw };
}

interface MarketplaceOrderHandoffDefaultsFormProps {
  value: MarketplaceOrderHandoffDefaults;
  onChange: (next: MarketplaceOrderHandoffDefaults) => void;
  disabled?: boolean;
  /** Order send allows editing Package fields prefilled for this order. */
  variant?: "org-settings" | "order-send";
  /** Org/package snapshot used to detect per-order edits. */
  baseline?: MarketplaceOrderHandoffDefaults;
  /** Limit to one handoff section (org drawer SettingsRow layout). */
  section?: "rules" | "brandKit";
  /** Render fields without the collapsible section chrome. */
  layout?: "accordion" | "plain";
}

function SourceBadge({ source }: { source: HandoffDefaultSource }) {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
          aria-label={t("marketplace.handoff.sourceAria", {
            source,
            defaultValue: "{{source}} source",
          })}
        >
          <Badge variant="slate">{source}</Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {t(SOURCE_I18N_KEYS[source], HANDOFF_SOURCE_MEANINGS[source])}
      </TooltipContent>
    </Tooltip>
  );
}

function FieldHint({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex cursor-pointer rounded-sm text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
          aria-label={t("marketplace.handoff.fieldHelpAria", "About this field")}
        >
          <Icon name="info" size="xs" color="inherit" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{text}</TooltipContent>
    </Tooltip>
  );
}

function HandoffField({
  item,
  value,
  onChange,
  disabled,
  variant,
  edited,
  compact,
}: {
  item: MarketplaceOrderHandoffDefaultItemMeta;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  variant: "org-settings" | "order-send";
  edited?: boolean;
  /** Single-line height that grows with content (org drawer settings rows). */
  compact: boolean;
}) {
  const { t } = useTranslation();
  const fieldId = `handoff-${variant}-${item.key}`;
  const warningId = `${fieldId}-warning`;
  const label = t(`marketplace.handoff.fields.${item.key}.label`, item.label);
  const description = t(`marketplace.handoff.fields.${item.key}.description`, item.description);
  const isFolder = item.source === "Folder";
  const showInternalUrlWarning = looksLikeHandoffUrl(value) && isVendorInaccessibleUrl(value);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex min-h-5 items-center gap-1.5">
        <Label htmlFor={fieldId} className="text-xs font-medium text-foreground">
          {label}
        </Label>
        <FieldHint text={description} />
        <SourceBadge source={item.source} />
        {edited ? (
          <Badge variant="status-sent">
            {t("marketplace.orders.list.brief.edited", "Edited for this order")}
          </Badge>
        ) : null}
      </div>
      <Textarea
        id={fieldId}
        rows={compact ? 1 : 2}
        maxLength={MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX}
        disabled={disabled}
        value={value}
        aria-describedby={showInternalUrlWarning ? warningId : undefined}
        className={cn(
          compact && "min-h-12 resize-none text-[13px] leading-normal field-sizing-content",
          showInternalUrlWarning && "border-warning-border",
        )}
        placeholder={
          isFolder
            ? t(
                "marketplace.handoff.folderPlaceholder",
                "Paste a URL, or write the text the partner should follow",
              )
            : variant === "org-settings"
              ? t(
                  "marketplace.handoff.packageOrgPlaceholder",
                  "Optional fallback when the partner package does not supply this",
                )
              : t(
                  "marketplace.handoff.packagePlaceholder",
                  "Filled from the selected partner package — edit for this order if needed",
                )
        }
        onChange={(event) => onChange(event.target.value)}
      />
      {showInternalUrlWarning ? (
        <p
          id={warningId}
          role="status"
          className="flex items-center gap-1.5 text-[11px] leading-relaxed text-warning-subtle-foreground"
        >
          <AlertTriangle className="size-3 shrink-0" aria-hidden />
          {t(
            "marketplace.sendHandoff.internalUrlWarning",
            "This looks like an internal link. Production partners usually cannot open it.",
          )}
        </p>
      ) : null}
    </div>
  );
}

function SectionFields({
  items,
  value,
  onChange,
  disabled,
  variant,
  baseline,
  compact,
}: {
  items: MarketplaceOrderHandoffDefaultItemMeta[];
  value: MarketplaceOrderHandoffDefaults;
  onChange: (next: MarketplaceOrderHandoffDefaults) => void;
  disabled: boolean;
  variant: "org-settings" | "order-send";
  baseline?: MarketplaceOrderHandoffDefaults;
  compact: boolean;
}) {
  return (
    <div className={cn("flex flex-col", compact ? "gap-4" : "gap-3")}>
      {items.map((item) => (
        <HandoffField
          key={item.key}
          item={item}
          value={value[item.key]}
          disabled={disabled}
          variant={variant}
          compact={compact}
          edited={
            Boolean(baseline) && value[item.key].trim() !== baseline![item.key].trim()
          }
          onChange={(next) => onChange({ ...value, [item.key]: next })}
        />
      ))}
    </div>
  );
}

function filledCount(
  items: MarketplaceOrderHandoffDefaultItemMeta[],
  value: MarketplaceOrderHandoffDefaults,
): number {
  return items.filter((item) => value[item.key].trim().length > 0).length;
}

function sectionItems(section: "rules" | "brandKit"): MarketplaceOrderHandoffDefaultItemMeta[] {
  return MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS.filter((item) => item.section === section);
}

/** "3 of 5 filled" pill for a settings-row heading; turns green once every field is filled. */
export function HandoffSectionProgress({
  section,
  value,
}: {
  section: "rules" | "brandKit";
  value: MarketplaceOrderHandoffDefaults;
}) {
  const { t } = useTranslation();
  const items = sectionItems(section);
  const filled = filledCount(items, value);
  const total = items.length;
  const isComplete = filled === total;

  return (
    <span
      data-testid={`handoff-section-progress-${section}`}
      className={cn(
        "w-fit rounded-full border px-2 py-1 text-[11px] font-medium leading-none",
        isComplete
          ? "border-transparent bg-success-subtle text-success-subtle-foreground"
          : "border-border bg-muted text-foreground",
      )}
    >
      {t("marketplace.handoff.sectionProgress", {
        filled,
        total,
        defaultValue: "{{filled}} of {{total}} filled",
      })}
    </span>
  );
}

const SECTION_EASE = "ease-[cubic-bezier(0.25,1,0.5,1)]";

function HandoffSettingsSection({
  id,
  open,
  onToggle,
  title,
  hint,
  badge,
  children,
}: {
  id: string;
  open: boolean;
  onToggle: () => void;
  title: string;
  hint: string;
  badge: string;
  children: ReactNode;
}) {
  const panelId = `${id}-panel`;

  return (
    <section className="border-t border-border first:border-t-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className={cn(
          "flex min-h-11 w-full cursor-pointer items-center gap-2 py-2.5 text-left",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-medium text-foreground">{title}</span>
          <span className="block text-[11px] leading-snug text-muted-foreground">{hint}</span>
        </span>
        <Badge variant="slate">{badge}</Badge>
        <Icon
          name="chevron-down"
          size="xs"
          color="muted"
          className={cn(
            "shrink-0 transition-transform duration-200",
            SECTION_EASE,
            "motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-label={title}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-200",
          SECTION_EASE,
          "motion-reduce:transition-none",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden px-1 -mx-1" inert={!open}>
          <div
            className={cn(
              "pb-3 transition-transform duration-200",
              SECTION_EASE,
              "motion-reduce:transition-none",
              open ? "translate-y-0" : "-translate-y-1",
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

export function MarketplaceOrderHandoffDefaultsForm({
  value,
  onChange,
  disabled = false,
  variant = "org-settings",
  baseline,
  section: sectionOnly,
  layout = "accordion",
}: MarketplaceOrderHandoffDefaultsFormProps) {
  const { t } = useTranslation();
  const sectionKeys = sectionOnly ? ([sectionOnly] as const) : (["rules", "brandKit"] as const);
  const sections = sectionKeys.map((section) => ({ section, items: sectionItems(section) }));
  const isOrderSend = variant === "order-send";
  const isPlain = layout === "plain";
  const [openSections, setOpenSections] = useState(() => {
    if (isOrderSend) {
      return { rules: false, brandKit: false };
    }
    return { rules: true, brandKit: filledCount(sectionItems("brandKit"), value) > 0 };
  });

  const sectionBlocks = sections.map(({ section, items }) => {
    const copy = SECTION_COPY[section];
    const filled = filledCount(items, value);
    const total = items.length;
    const edits = baseline ? countHandoffSectionEdits(value, baseline) : null;
    const edited = section === "rules" ? (edits?.rules ?? 0) : (edits?.brandKit ?? 0);
    const badge =
      edited > 0
        ? t("marketplace.handoff.filledCountEdited", {
            filled,
            total,
            edited,
            defaultValue: "{{filled}}/{{total}} · {{edited}} edited",
          })
        : t("marketplace.handoff.filledCount", {
            filled,
            total,
            defaultValue: "{{filled}}/{{total}}",
          });

    const fields = (
      <SectionFields
        items={items}
        value={value}
        onChange={onChange}
        disabled={disabled}
        variant={variant}
        baseline={baseline}
        compact={isPlain}
      />
    );

    if (isPlain) {
      return <div key={section}>{fields}</div>;
    }

    return (
      <HandoffSettingsSection
        key={section}
        id={`${variant}-handoff-${section}`}
        open={openSections[section]}
        onToggle={() =>
          setOpenSections((current) => ({ ...current, [section]: !current[section] }))
        }
        title={t(copy.titleKey, copy.titleDefault)}
        hint={t(copy.hintKey, copy.hintDefault)}
        badge={badge}
      >
        {fields}
      </HandoffSettingsSection>
    );
  });

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col">
        {isOrderSend ? (
          <p className="pb-1.5 text-[11px] leading-relaxed text-muted-foreground">
            {t(
              "marketplace.sendHandoff.briefHint",
              "Prefilled from organisation settings. Expand a section to edit this order only.",
            )}
          </p>
        ) : null}
        <div className={cn("flex flex-col", layout === "accordion" && "border-t border-border")}>
          {sectionBlocks}
        </div>
      </div>
    </TooltipProvider>
  );
}

interface OrderHandoffOverrideNoteFieldProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

export function OrderHandoffOverrideNoteField({
  value,
  onChange,
  disabled = false,
}: OrderHandoffOverrideNoteFieldProps) {
  const { t } = useTranslation();
  const fieldId = "handoff-override-note";

  return (
    <TooltipProvider delayDuration={200}>
      <section className="flex flex-col gap-1.5">
        <div className="flex min-h-5 items-center gap-1.5">
          <Label htmlFor={fieldId} className="text-xs font-medium text-muted-foreground">
            {t("marketplace.sendHandoff.orderNoteTitle", "Order-specific instructions")}
          </Label>
          <SourceBadge source="Per order" />
        </div>
        <Textarea
          id={fieldId}
          rows={4}
          maxLength={MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX}
          disabled={disabled}
          value={value}
          placeholder={t(
            "marketplace.sendHandoff.orderNotePlaceholder",
            "e.g. Use the Q2 brand kit folder instead of the default for this send.",
          )}
          onChange={(event) => onChange(event.target.value)}
        />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {t(
            "marketplace.sendHandoff.orderNoteHint",
            "Optional. Shown at the top of the production partner email and overrides conflicting org defaults for this order.",
          )}
        </p>
      </section>
    </TooltipProvider>
  );
}
